import { Box, Button, Card, Container, Divider, Grid, List, ListItem, ListItemIcon, ListItemText, Paper, Table, TableBody, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { enqueueSnackbar } from "notistack";
import axios, { endpoints } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { Camera, Device, EldVehicle } from "src/models";
import useModelData, { deleteById, replaceById } from "src/hooks/use-model-data";
import Iconify from "src/components/iconify/iconify";
import { DisplayPageData } from "src/components/display-page-data";
import useModelSavingData from "src/hooks/use-model-saving-data";

import PageHeader from "src/components/page-header";
import NoneFound from "src/components/none-found";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import { TruxTableCell } from "src/components/trux-table-cell";
import { useRouter } from "src/routes/hooks";
import { ColoredIcon } from "src/components/colored-icon";
import { useSearch } from "src/hooks/use-search";
import { SearchCard } from "src/components/search-card";
import { Constants } from "src/constants";
import { formatDecimalTwoAsString } from "src/utils/decimal-two-utils";
import QRCode from 'qrcode';
import { Document, Page as PdfPage, Image, pdf, Text, View } from "@react-pdf/renderer";
import Label from "src/components/label";
import { Violations } from "src/components/violations";
import DisplayErrors from "src/components/display-errors";
import SimpleDialog from "src/components/simple-dialog";
import TruxFormField from "src/components/inputs/trux-form-field";
import TruxAutocompleteField from "src/components/inputs/trux-autocomplete-field";
import { select } from "src/theme/overrides/components/select";

type CameraWithDevice = Camera & {
  device?: Device
}

type CamerasProps = {
  cameras?: CameraWithDevice[],
  onCameraSelected: (a: Camera) => void
}

const Cameras = memo(({ cameras, onCameraSelected }: CamerasProps) => {
  const { sortedList, sortData } = useSort(cameras);

  const {
    searchText,
    setSearchText,
    filteredList,
  } = useSearch(sortedList, ["imei"]);

  if (!sortedList) {
    return null;
  }

  return <div>
    <SearchCard searchText={searchText} setSearchText={setSearchText} searchingFor="Cameras" />

    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
              <SortableHeader sortKey="imei" sortData={sortData} >
                IMEI
              </SortableHeader>

              <TruxTableCell>
                Assigned to Vehicle
              </TruxTableCell>
            </TableRow>
          </TableHead>


          <TableBody>
            {filteredList.map((camera, index) =>
            (<TableRow
              key={index}
              hover
              onClick={() => onCameraSelected(camera)}
              sx={{ cursor: "pointer" }}
            >
              <TruxTableCell>
                {camera.imei}
              </TruxTableCell>

              <TruxTableCell>
                {camera.device ? camera.device.deviceType : "Not Assigned"}
              </TruxTableCell>
            </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});


type PageModel = {
  cameras?: CameraWithDevice[],
  eldVehicles?: EldVehicle[],
}

export function NoCamerasFound() {
  return <NoneFound title="AI CAMERA = ADAS + DMS">
    <Box display="flex" flexDirection="column" width="100%">
      <Box sx={{ display: "flex", flexDirection: { xs: 'column', md: 'row' }, gap: 4, alignItems: 'flex-start' }}>
        {/* Features Section */}
        <Box sx={{ flex: 2 }}>
          <Grid container spacing={3}>
            {/* ADAS Features */}
            <Grid item xs={12} sm={6}>
              <Paper elevation={2} sx={{ p: 3, height: '100%', bgcolor: 'primary.50' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                  ADAS Features
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Advanced Driver Assistance System
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <ColoredIcon color="success">
                        <Iconify icon="eva:checkmark-circle-2-fill" width={24} />
                      </ColoredIcon>
                    </ListItemIcon>
                    <ListItemText
                      primary="Lane Departure Warning"
                      secondary="Alerts when vehicle drifts from lane"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <ColoredIcon color="success">
                        <Iconify icon="eva:checkmark-circle-2-fill" width={24} />
                      </ColoredIcon>
                    </ListItemIcon>
                    <ListItemText
                      primary="Forward Collision Warning"
                      secondary="Detects potential front collisions"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <ColoredIcon color="success">
                        <Iconify icon="eva:checkmark-circle-2-fill" width={24} />
                      </ColoredIcon>
                    </ListItemIcon>
                    <ListItemText
                      primary="Harsh Braking Detection"
                      secondary="Monitors sudden stops and deceleration"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <ColoredIcon color="success">
                        <Iconify icon="eva:checkmark-circle-2-fill" width={24} />
                      </ColoredIcon>
                    </ListItemIcon>
                    <ListItemText
                      primary="Following Distance Alert"
                      secondary="Maintains safe distance from vehicles"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            {/* DMS Features */}
            <Grid item xs={12} sm={6}>
              <Paper elevation={2} sx={{ p: 3, height: '100%', bgcolor: 'secondary.50' }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'secondary.main', fontWeight: 'bold' }}>
                  DMS Features
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Driver Monitoring System
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <ColoredIcon color="success">
                        <Iconify icon="eva:checkmark-circle-2-fill" width={24} />
                      </ColoredIcon>
                    </ListItemIcon>
                    <ListItemText
                      primary="Seatbelt Detection"
                      secondary="Ensures driver wears seatbelt"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <ColoredIcon color="success">
                        <Iconify icon="eva:checkmark-circle-2-fill" width={24} />
                      </ColoredIcon>
                    </ListItemIcon>
                    <ListItemText
                      primary="Phone Usage Detection"
                      secondary="Alerts when driver uses phone"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <ColoredIcon color="success">
                        <Iconify icon="eva:checkmark-circle-2-fill" width={24} />
                      </ColoredIcon>
                    </ListItemIcon>
                    <ListItemText
                      primary="Smoking Detection"
                      secondary="Monitors smoking while driving"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <ColoredIcon color="success">
                        <Iconify icon="eva:checkmark-circle-2-fill" width={24} />
                      </ColoredIcon>
                    </ListItemIcon>
                    <ListItemText
                      primary="Distracted Driving Alert"
                      secondary="Detects drowsiness & inattention"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            {/* Key Benefits */}
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 3, bgcolor: 'grey.50' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Key Benefits
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="#32bb40" sx={{ fontWeight: 'bold' }}>
                        85%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Reduction in accidents
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="#32bb40" sx={{ fontWeight: 'bold' }}>
                        24/7
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Real-time monitoring
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="#32bb40" sx={{ fontWeight: 'bold' }}>
                        AI
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Powered intelligence
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Image Section */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box
            component="img"
            src="https://accountingimageuploads.s3.us-west-2.amazonaws.com/371-1.png"
            alt="AI Dashcam Device"
            sx={{
              width: '100%',
              maxWidth: 360,
              height: 'auto',
            }}
          />
          <Box sx={{ p: 2, width: '100%', textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              Professional AI Dashcam
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Dual-camera system with advanced AI algorithms for comprehensive fleet safety management
            </Typography>
            <Button
              variant="contained"
              color="success"
              href="mailto:sales@countingtrucks.com"
              fullWidth
            >
              Contact Sales
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  </NoneFound>;
}

export default function Page() {
  const settings = useSettingsContext();
  const router = useRouter();
  const dataLoader = useCallback(() => ({
    cameras: axios.get(endpoints.cameras.findAll).then(response => response.data),
    eldVehicles: axios.get(endpoints.eldVehicles.findAll).then(response => response.data),
  }), []);
  const {
    data,
    updateData,
    setData,
    error,
    loading,
    setShouldRefresh
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      console.log(error);
      alert(error);
    }
  }, [loading, error]);

  const [isOpen, setIsOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);

  const [selectedCamera, setSelectedCamera] = useState<CameraWithDevice>();

  const selectedVehicle = data?.eldVehicles?.find(eldVehilce => eldVehilce.deviceId === selectedCamera?.device?.id);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {data && data?.cameras?.length !== 0 &&
          <PageHeader title="Cameras" />
        }

        {data && data?.cameras?.length === 0 &&
          <NoCamerasFound />
        }

        {data && data?.cameras?.length !== 0 && <Cameras cameras={data.cameras} onCameraSelected={(camera: CameraWithDevice) => {
          setSelectedCamera({...camera});
          setIsOpen(true);
        }} />}

        {isOpen && <SimpleDialog
            title="Assign Camera"
            open={isOpen}
            onCancel={() => setIsOpen(false)}
            onSubmit={() => {
              setDialogLoading(true);

              axios.post(endpoints.cameras.assign, {
                deviceId: selectedCamera?.device?.id || 0,
                cameraId: selectedCamera?.id || 0,
              }).then(() => {
                enqueueSnackbar("Successfully Assigned the Camera!");
                setShouldRefresh(true);
                setIsOpen(false);
              }).finally(() => {
                setDialogLoading(false);
              })
            }}
            submitDisabled={dialogLoading}
            submitButton="Assign Camera"
            submitButtonIcon={<Iconify icon="basil:save-solid" />}
            submitButtonColor="info">

            <TruxFormField label="Truck">
              <TruxAutocompleteField
                options={data?.eldVehicles?.map(eldVehicle => ({
                  label: `${eldVehicle.vehicleUnitNumber} - ${eldVehicle.vin}`,
                  id: eldVehicle.id
                })) || []}
                onClear={() => {
                  if (selectedCamera) {
                    setSelectedCamera({...selectedCamera, device: undefined});
                  }
                }}
                onChange={(value) => {
                  console.log(value)
                  if (selectedCamera) {
                    if (value == null) {
                      setSelectedCamera((current) => (!current ? current : {...current}));
                    } else {
                      data?.eldVehicles?.forEach(eldVehicle => {
                        if (value.id === eldVehicle.id) {
                          setSelectedCamera({...selectedCamera, device: {
                            id: eldVehicle.deviceId,
                            deviceType: `ELD Truck #${eldVehicle.vehicleUnitNumber}`
                          }as Device });
                        }
                      })
                    }
                  }
                }}
                selected={{
                  id: selectedVehicle ? selectedVehicle.id : 0,
                  label: selectedVehicle ? `${selectedVehicle.vehicleUnitNumber} - ${selectedVehicle.vin}` : `Not Assigned`,
                }}
              />
            </TruxFormField>
          </SimpleDialog>
        }
      </DisplayPageData>
    </Container>
  );
}
